import React, { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import ImageUpload from "../../../CoreComponent/ImageUpload";
import DateInput from "../../../CoreComponent/Date";
import "./style.scss";
import httpService from "../../../common/httpService";
import SimpleLabelValue from "../../SimpleLabelValue";
import { useAuth } from "../../../common/AuthContext";
import { backendUrlImages } from "../../../constant/config";
import ViewVisaLinkButton from "./ViewVisaLinkButton";
import ViewVisaLinkButtonTwo from "./ViewVisaLinkButtonTwo";

const VisaPage = () => {
  const { userId, myConferenceId } = useAuth();
  const BaseUrl = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showVisaForm, setShowVisaForm] = useState(false);
  const [passportImage, setPassportImage] = useState(null);
  const [arrivalDate, setArrivalDate] = useState("");
  const [departureDate, setDepartureDate] = useState("");
  const [error, setError] = useState("");
  const [visapdf, setVisapdf] = useState(null);
  const [visaPrice, setVisaPrice] = useState(0);
  const [isVisaCovered, setIsVisaCovered] = useState(false);

  const [visaData, setVisaData] = useState(null);
  const [providerData, setProviderData] = useState(null);

  const [showVisaPopup, setShowVisaPopup] = useState(false);
  const [visaPopupData, setVisaPopupData] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);

  const token = localStorage.getItem("token");

  const isNonEmptyObject = (data) =>
    data && typeof data === "object" && !Array.isArray(data) && Object.keys(data).length > 0;

  const normalizeProviderData = (data) => {
    if (!data) return null;
    if (data.visa) return data.visa;
    if (data.data?.visa) return data.data.visa;
    if (Array.isArray(data.data) && data.data.length > 0) return data.data[0];
    if (data.data && typeof data.data === "object") return data.data;
    if (Array.isArray(data) && data.length > 0) return data[0];
    if (typeof data === "object") return data;
    return null;
  };

  const fetchProviderVisa = async () => {
    if (!userId) {
      setProviderData(null);
      return;
    }

    try {
      const res = await axios.get(`https://eventscons.com/eventbackendnew/api/visa/${userId}`);
      const mapped = normalizeProviderData(res.data);

      if (isNonEmptyObject(mapped)) {
        setProviderData(mapped);
        setVisaData(mapped);
        setVisapdf(mapped.visapdf || mapped.visa_pdf || null);

        if (mapped.arrival_date) setArrivalDate(mapped.arrival_date);
        if (mapped.departure_date) setDepartureDate(mapped.departure_date);
      } else {
        setProviderData(null);
        setVisaData(null);
        setVisapdf(null);
      }
    } catch (err) {
      setProviderData(null);
      setVisaData(null);
      setVisapdf(null);
    }
  };

  const getConferenceById = async () => {
    if (!myConferenceId) return;
    try {
      const res = await axios.get(`${BaseUrl}/con/id/${myConferenceId}`);
      setVisaPrice(res.data?.conference?.visa_price || 0);
    } catch (err) {
      console.error("Error fetching Conference details", err);
    }
  };

  const fetchInternalVisa = async () => {
    if (!token) return;

    try {
      const data = await httpService({
        method: "GET",
        url: `${BaseUrl}/visa`,
        headers: { Authorization: `Bearer ${token}` },
        onSuccess: (response) => response,
        onError: (err) => {
          throw err;
        },
        withToast: false,
      });

      const v = data?.visa || data || null;

      if (isNonEmptyObject(v)) {
        setVisaData(v);
        setVisapdf(v.visapdf || v.visa_pdf || null);
        setArrivalDate(v.arrival_date || "");
        setDepartureDate(v.departure_date || "");
        setShowVisaForm(false);
      } else {
        setVisaData(null);
        setVisapdf(null);
      }
    } catch (err) {
      console.error("Error fetching internal visa data:", err);
    }
  };

  const fetchSpeakerVisaFee = async () => {
    if (!userId) return;

    try {
      const res = await axios.get(`https://eventscons.com/eventbackendnew/api/speaker/visa/fee/${userId}`);
      const apiData = res.data;

      const visaFees =
        apiData?.visa_fees ??
        apiData?.data?.visa_fees ??
        apiData?.visa?.visa_fees ??
        apiData?.data?.visa?.visa_fees ??
        null;

      const covered = typeof visaFees === "string" && visaFees.toLowerCase() === "covered";
      setIsVisaCovered(covered);

      if (covered) {
        setVisaPrice(0);
        setVisaData((prev) => (prev ? { ...prev, visa_cost: "0" } : prev));
        setProviderData((prev) => (prev ? { ...prev, visa_cost: "0" } : prev));
      }
    } catch (err) {
      console.error("Error fetching speaker visa fee", err);
    }
  };

  useEffect(() => {
    getConferenceById();
  }, [myConferenceId]);

  useEffect(() => {
    fetchProviderVisa();
    fetchInternalVisa();
    fetchSpeakerVisaFee();
  }, [userId]);

  const handleUserChoice = (choice) => {
    if (choice === "yes") {
      setShowVisaForm(true);
    } else {
      setShowVisaForm(false);
      navigate("/flight/form");
    }
  };

  const handleVisaCheck = async () => {
    if (!userId) {
      toast.error("User ID not found.");
      return;
    }

    setShowVisaPopup(true);
    setPopupLoading(true);

    try {
      const res = await axios.get(`https://eventscons.com/eventbackendnew/api/visa/${userId}`);
      const mapped = normalizeProviderData(res.data);

      if (mapped?.last_url || mapped?.url || mapped?.visa_url) {
        setVisaPopupData({
          last_url: mapped.last_url || mapped.url || mapped.visa_url,
        });
      } else {
        setVisaPopupData({ last_url: null });
      }
    } catch (err) {
      setVisaPopupData({ last_url: null });
    } finally {
      setPopupLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("user_id", userId);
      formData.append("passport_image", passportImage);
      formData.append("arrival_date", arrivalDate);
      formData.append("departure_date", departureDate);
      formData.append("visa_cost", isVisaCovered ? 0 : visaPrice);

      await axios.post(`${BaseUrl}/visa`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Visa data saved successfully");
      await fetchProviderVisa();
      await fetchInternalVisa();
      setShowVisaForm(false);
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred");
      toast.error(err.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    const finalCost = isVisaCovered ? 0 : Number(visaData?.visa_cost || visaPrice || 0);

    if (finalCost === 0) {
      toast.success("Visa fee is covered, no payment required.");
      return;
    }

    if (!visaData?.id) {
      toast.error("Visa record not found");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(
        `${BaseUrl}/paypal/create-payment`,
        {
          amount: finalCost,
          return_url: `https://eventscons.com/success/visa/${visaData.id}/0`,
          cancel_url: `https://eventscons.com/failed`,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const orderID = res.data?.id;
      if (orderID) {
        window.location.href = `https://www.paypal.com/checkoutnow?token=${orderID}`;
      } else {
        toast.error("Payment order ID not received");
      }
    } catch (err) {
      toast.error("Payment creation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const idToDelete = providerData?.id || visaData?.id;

    if (!idToDelete) {
      toast.error("No visa record found to delete.");
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this visa request?");
    if (!confirmed) return;

    try {
      setLoading(true);

      const response = await axios.delete(
        `https://eventscons.com/eventbackendnew/api/visa/delete/${idToDelete}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (response.status === 200 || response.status === 204) {
        toast.success("Visa request deleted successfully");
        setProviderData(null);
        setVisaData(null);
        setVisapdf(null);
        setArrivalDate("");
        setDepartureDate("");
        setShowVisaForm(false);
      } else {
        toast.error("Delete failed");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Error deleting visa request"
      );
    } finally {
      setLoading(false);
    }
  };

  const showInfoSection = isNonEmptyObject(providerData) || isNonEmptyObject(visaData);
  const displayVisaCost = isVisaCovered ? 0 : Number(visaData?.visa_cost ?? providerData?.visa_cost ?? visaPrice ?? 0);
  const showPayButton = displayVisaCost > 0;

  return (
    <div className="visa-page-container">
      {!showInfoSection && !showVisaForm && (
        <div className="question-container">
          <div className="button-group">
            <ViewVisaLinkButtonTwo userId={userId} />
          </div>

          <h2>Would you like the organizing company to handle the visa for you?</h2>

          <div className="button-group">
            <button className="yes-btn" onClick={() => handleUserChoice("yes")}>
              Yes
            </button>
            <button className="no-btn" onClick={() => handleUserChoice("no")}>
              No
            </button>
          </div>
        </div>
      )}

      {showVisaPopup && (
        <div className="popup-overlay">
          <div
            className="popup-content"
            style={{ minWidth: "500px", maxWidth: "600px", position: "relative" }}
          >
            <button
              className="popup-close"
              onClick={() => setShowVisaPopup(false)}
              style={{ position: "absolute", top: "10px", right: "15px", fontSize: "24px" }}
            >
              ✖
            </button>

            {popupLoading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <p>Loading visa information...</p>
              </div>
            ) : visaPopupData?.last_url ? (
              <>
                <div style={{ margin: "20px 0" }}>
                  <label style={{ fontWeight: "600", display: "block", marginBottom: "10px" }}>
                    Visa Link:
                  </label>
                  <input
                    type="text"
                    value={visaPopupData.last_url}
                    readOnly
                    style={{
                      width: "100%",
                      padding: "15px",
                      border: "2px solid #4caf50",
                      borderRadius: "25px",
                      opacity: "0.9",
                      fontSize: "14px",
                      textAlign: "center",
                      background: "#f1f8e9",
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: "15px", justifyContent: "center", flexWrap: "wrap" }}>
                  <button
                    onClick={() => window.open(visaPopupData.last_url, "_blank")}
                    style={{
                      background: "#4caf50",
                      color: "white",
                      padding: "15px 30px",
                      border: "none",
                      borderRadius: "25px",
                      fontWeight: "bold",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  >
                    Open Visa Link
                  </button>

                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(visaPopupData.last_url);
                      toast.success("Visa link copied to clipboard!");
                    }}
                    style={{
                      background: "#2196f3",
                      color: "white",
                      padding: "15px 30px",
                      border: "none",
                      borderRadius: "25px",
                      fontWeight: "bold",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  >
                    Copy Link
                  </button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <h3 style={{ color: "#d32f2f" }}>Visa link not provided yet.</h3>
                <p>Contact your event organizer for visa invitation letter.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showVisaForm && (
        <form onSubmit={handleSubmit} className="visa-form" style={{ width: "600px" }}>
          <div className="fields-container">
            <ImageUpload
              label="Upload Passport Image"
              inputValue={passportImage}
              setInputValue={setPassportImage}
              allowedExtensions={["jpg", "jpeg", "png", "pdf"]}
              required
            />
            <DateInput
              label="Arrival Date"
              placeholder="YYYY-MM-DD"
              inputValue={arrivalDate}
              setInputValue={setArrivalDate}
              required
            />
            <DateInput
              label="Departure Date"
              placeholder="YYYY-MM-DD"
              inputValue={departureDate}
              setInputValue={setDepartureDate}
              required
            />
          </div>

          <button
            type="submit"
            className={`submit-btn ${!arrivalDate || !departureDate || !passportImage || loading ? "disabled" : ""}`}
            disabled={!arrivalDate || !departureDate || !passportImage || loading}
          >
            {loading ? (
              <span>
                <span className="spinner" /> Submitting...
              </span>
            ) : (
              "Submit"
            )}
          </button>
        </form>
      )}

      {isNonEmptyObject(providerData) && (
        <Fragment>
          <h2 className="title-visa-2">User Data From API</h2>
          <div className="visa-info">
            {providerData.id && <SimpleLabelValue label="ID" value={providerData.id} />}
            {providerData.user_id && <SimpleLabelValue label="User ID" value={providerData.user_id} />}
            {providerData.name && <SimpleLabelValue label="Name" value={providerData.name} />}
            {providerData.full_name && <SimpleLabelValue label="Full Name" value={providerData.full_name} />}
            {providerData.email && <SimpleLabelValue label="Email" value={providerData.email} />}
            {providerData.phone && <SimpleLabelValue label="Phone" value={providerData.phone} />}
            {providerData.status && <SimpleLabelValue label="Status" value={providerData.status} />}
            {providerData.arrival_date && <SimpleLabelValue label="Arrival Date" value={providerData.arrival_date} />}
            {providerData.departure_date && <SimpleLabelValue label="Departure Date" value={providerData.departure_date} />}
            {providerData.visa_cost !== undefined && <SimpleLabelValue label="Visa Cost" value={providerData.visa_cost} />}
          </div>
        </Fragment>
      )}

      {isNonEmptyObject(visaData) &&
        (visaData.payment_status === "completed" ? (
          <div className="payment-success">
            <h2>Payment Successfully Completed!</h2>
            <p>Your visa application will be processed soon.</p>
          </div>
        ) : (
          <Fragment>
            <h2 className="title-visa-2">Visa Information</h2>
            <div className="visa-info">
              {visaData.arrival_date && <SimpleLabelValue label="Arrival Date" value={visaData.arrival_date} />}
              {visaData.departure_date && <SimpleLabelValue label="Departure Date" value={visaData.departure_date} />}
              <SimpleLabelValue label="Status" value={visaData.status} />
              <SimpleLabelValue label="Visa Cost (USD)" value={displayVisaCost} />

              {(visaData.last_url ||
                visaData.url ||
                visaData.visa_url ||
                providerData?.last_url ||
                providerData?.url ||
                providerData?.visa_url) && (
                <div className="download-link" style={{ marginTop: "15px" }}>
                  <button
                    type="button"
                    className="next-button"
                    onClick={() =>
                      window.open(
                        visaData.last_url ||
                          visaData.url ||
                          visaData.visa_url ||
                          providerData?.last_url ||
                          providerData?.url ||
                          providerData?.visa_url,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  >
                    VISA Link
                  </button>
                </div>
              )}

              {visapdf && (
                <div className="download-link" style={{ marginTop: "15px" }}>
                  <a
                    href={`${backendUrlImages}${visapdf}`}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download Visa PDF
                  </a>
                </div>
              )}
            </div>

            <div className="actions-section">
              <button className="next-button" type="button" onClick={() => navigate("/flight/form")}>
                Next
              </button>
              <button className="next-button" type="button" onClick={handleDelete}>
                Delete
              </button>
              <ViewVisaLinkButton userId={userId} />
              {showPayButton && (
                <button className="next-button" type="button" onClick={handlePayment}>
                  Pay
                </button>
              )}
            </div>
          </Fragment>
        ))}

      {error && <p style={{ color: "red", marginTop: "12px" }}>{error}</p>}
    </div>
  );
};

export default VisaPage;